import {useNavigate, useParams, useLocation} from "react-router-dom";
import { calculateAge } from '../utils/age';
import {
  Box, IconButton, Typography,
} from "@mui/material";
import {
  ArrowForward,
  ManageAccounts as ManageAccountsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  Schedule as ScheduleIcon,
  AutoAwesomeOutlined as AutoAwesomeIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useState} from "react";
import moment from "moment";

import ClientTabs from "../componenets/ClientTabs";
import ClientPageMainSection from "../componenets/ClientPageMainSection";
import ClientPageProfile from "../componenets/ClientPageProfile";
import ClientPageNotes from "../componenets/client/ClientPageNotes";
import ClientPageTargets from "../componenets/client/ClientPageTargets";
import ClientPageForms from "../componenets/client/ClientPageForms";
import ClientPageCheckIns from "../componenets/client/ClientPageCheckIns";
import ClientPageScheduledCheckIns from "../componenets/client/ClientPageScheduledCheckIns";
import ClientActionMenu from "../componenets/client/ClientActionMenu";
import {StatusChip} from "../componenets/ui";
import {getClient, getClientNotes} from "../services/TraineeService";
import {getTraineeCheckIns} from "../services/CheckInService";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {experienceLevels} from "../enums/ExperienceLevels";
import {colors, borders} from "../componenets/ui/colors";
import {formatPhoneForDisplay} from "../componenets/shared/PhoneInput";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";

/* ── Quick Info Pill ─────────────────────────────────────── */
const InfoPill = ({icon: Icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
      <Icon sx={{color: colors.textMuted, fontSize: 16}}/>
      <Typography sx={{fontSize: "12px", fontWeight: 500, color: colors.textMuted}}>{label}:</Typography>
      <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textSecondary}}>{value}</Typography>
    </Box>
);

/* ═══════════════════════════════════════════════════════════
   Client Detail Page
   ═══════════════════════════════════════════════════════════ */
const ClientPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const {notificationHandler} = useMessageContext();

  const [tabValue, setTabValue] = useState("one");
  const [client, setClient] = useState(null);
  const [reload, setReload] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [hasCheckIns, setHasCheckIns] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleReload = useCallback(() => setReload(p => !p), []);

  // Everyone defaults to the new (v2) design — unless they explicitly opted into the classic view.
  const optedClassic = new URLSearchParams(location.search).get("classic") === "1";
  useEffect(() => {
    if (!optedClassic) navigate(`/app/clients/${params.userId}/v2`, {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data fetching ──────────────────────────────────────
  useEffect(() => {
    getClient(params.userId)
        .then(r => setClient(r.data))
        .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  }, [reload]);

  useEffect(() => {
    getClientNotes(params.userId)
        .then(r => setHasNotes(r.data.length > 0))
        .catch(() => {});
  }, [reload]);

  useEffect(() => {
    getTraineeCheckIns(params.userId)
        .then(r => setHasCheckIns((r.data || []).some(c => c.status === "PENDING" || c.status === "COMPLETED")))
        .catch(() => {});
  }, [reload]);

  // ── Helpers ────────────────────────────────────────────
  const age = client?.dateOfBirth ? calculateAge(client.dateOfBirth) : null;
  const genderText = client?.gender === "MALE" ? "זכר" : client?.gender === "FEMALE" ? "נקבה" : null;
  const expLevel = client?.experienceLevel ? experienceLevels.find(l => l.value === client.experienceLevel)?.name : null;
  const lastTrained = client?.lastTrained ? moment.unix(client.lastTrained).fromNow() : null;

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", height: "100%", bgcolor: colors.bg}}>

        {/* ─── Header ──────────────────────────────────── */}
        <Box sx={{
          bgcolor: colors.white, borderBottom: borders.default,
          px: {xs: 2, md: 3}, pt: {xs: 1.5, md: 2}, pb: {xs: 1, md: 1.5},
          flexShrink: 0, direction: "rtl",
        }}>
          {/* Title row */}
          <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1}}>
            <IconButton
                onClick={() => navigate("/app/clients")}
                sx={{color: colors.textSecondary, p: 1, "&:hover": {bgcolor: colors.bgHover, color: colors.textPrimary}}}
            >
              <ArrowForward/>
            </IconButton>

            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2}}>
              {client?.name || "פרטי לקוח"}
            </Typography>

            {client && (
                client.enabled
                    ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                    : <StatusChip label="חסום" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>
            )}

            <Box sx={{flex: 1}}/>

            {/* New design entry */}
            <Box component="button" onClick={() => navigate(`/app/clients/${params.userId}/v2`)} sx={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              background: "linear-gradient(135deg, #01BBA7, #019385)", color: "#fff",
              height: 38, px: "15px", borderRadius: borders.radius.md,
              fontSize: "13px", fontWeight: 600,
              border: "none", cursor: "pointer", fontFamily: "inherit",
              boxShadow: "0 4px 14px rgba(1,187,167,.35)",
              transition: "all .14s",
              "&:hover": {filter: "brightness(1.05)", transform: "translateY(-1px)"},
            }}>
              <AutoAwesomeIcon sx={{fontSize: 16}}/>
              עיצוב חדש
            </Box>

            {/* 3-dot ActionMenu */}
            {client && (
                <IconButton
                    onClick={e => setMenuAnchor(e.currentTarget)}
                    sx={{
                      color: colors.textSecondary, border: borders.default,
                      borderRadius: borders.radius.md, width: 40, height: 40,
                      "&:hover": {bgcolor: colors.bgHover, borderColor: colors.borderHover},
                    }}
                >
                  <ManageAccountsIcon/>
                </IconButton>
            )}
          </Box>

          {/* Quick info pills */}
          {client && (
              <Box sx={{display: "flex", gap: {xs: 1.5, md: 3}, flexWrap: "wrap", pr: 6.5}}>
                {client.email && <InfoPill icon={EmailIcon} label="מייל" value={client.email}/>}
                {client.phoneNumber && <InfoPill icon={PhoneIcon} label="טלפון" value={formatPhoneForDisplay(client.phoneNumber)}/>}
                {age && <InfoPill icon={CakeIcon} label="גיל" value={age}/>}
                {genderText && <InfoPill icon={GenderIcon} label="מגדר" value={genderText}/>}
                {expLevel && <InfoPill icon={ScheduleIcon} label="רמה" value={expLevel}/>}
                {lastTrained && <InfoPill icon={ScheduleIcon} label="אימון אחרון" value={lastTrained}/>}
              </Box>
          )}
        </Box>

        {/* ─── Tabs + Content ──────────────────────────── */}
        <Box sx={{flex: 1, overflow: "auto", px: {xs: 1.5, md: 3}, pb: 3}}>
          <ClientTabs hasNotes={hasNotes} hasCheckIns={hasCheckIns} tabValue={tabValue} setTabValue={setTabValue}/>
          {tabValue === "one" && <ClientPageMainSection userId={params.userId}/>}
          {tabValue === "two" && <ClientPageProfile handleReload={handleReload} client={client}/>}
          {tabValue === "three" && <ClientPageTargets client={client}/>}
          {tabValue === "four" && client && <ClientPageForms client={client}/>}
          {tabValue === "five" && client && (
              <ClientPageNotes client={client} onNotesChanged={setHasNotes}/>
          )}
          {tabValue === "six" && client && (
            <>
              <ClientPageCheckIns client={client}/>
              <ClientPageScheduledCheckIns client={client}/>
            </>
          )}
        </Box>

        {client && (
            <ClientActionMenu
                trainee={client}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onChanged={() => {
                  setMenuAnchor(null);
                  handleReload();
                }}
                onDeleted={() => {
                  setMenuAnchor(null);
                  navigate("/app/clients");
                }}
            />
        )}
      </Box>
  );
};

export default ClientPage;
